import {cloneDeep, merge} from 'lodash';

import {PaletteColorsEnum} from '../../../../core/src/models/api/paletteModel';
import {alphas, greys, greysDark, paletteDark} from '../../styles/colorStyles';
import {colorPrimitive} from '../../styles/primitives/colorPrimitive';
import {lightBorderSemanticStyles, redesignedDarkBorderSemanticStyles} from './borderSemanticStyles';
import {darkTypographySemanticStyles, lightTypographySemanticStyles} from './typographySemanticStyles';

export interface CellSemanticStyles {
  conversationPreviewBackground: string;
  summaryUnreadText: string;
  selectedBackgroundBubble: string;
  selectedPrecogDots: string;
  unreadPreviewBackground: string;
  overlayBackground: string;
  dividerColor: string;
  unreadIndicator: {
    backgroundColor: string;
    hoverBackgroundColor: string;
  };
  background: {
    default: string;
    hover: string;
    selected: string;
  };
  border: {
    default: string;
    selected: string;
  };
}

export const lightCellSemanticStyles = {
  conversationPreviewBackground: greys.white,
  summaryUnreadText: greys.shade80,
  selectedBackgroundBubble: alphas.white30,
  selectedPrecogDots: greys.white,
  unreadPreviewBackground: '#cce1ff', // One-off color
  overlayBackground: alphas.white60,
  dividerColor: alphas.transparent,
  unreadIndicator: {
    backgroundColor: colorPrimitive.blue[700],
    hoverBackgroundColor: colorPrimitive.blue[300],
  },
  background: {
    default: greys.white,
    hover: greys.shade20,
    selected: greys.shade20,
  },
  border: {
    default: alphas.transparent,
    selected: alphas.transparent,
  },
} as const satisfies CellSemanticStyles;

export const darkCellSemanticStyles: CellSemanticStyles = {
  conversationPreviewBackground: greysDark.shade30,
  summaryUnreadText: alphas.white50,
  selectedBackgroundBubble: alphas.white30,
  selectedPrecogDots: greysDark.black,
  unreadPreviewBackground: paletteDark[PaletteColorsEnum.BLUE].shade30,
  overlayBackground: `rgba(0, 0, 0, .9)`, // One-off color
  dividerColor: alphas.transparent,
  unreadIndicator: {
    backgroundColor: colorPrimitive.blue[700],
    hoverBackgroundColor: colorPrimitive.blue[300],
  },
  background: {
    default: greysDark.white,
    hover: greysDark.shade20,
    selected: greysDark.shade20,
  },
  border: {
    default: alphas.transparent,
    selected: alphas.transparent,
  },
} as const satisfies CellSemanticStyles;

export const redesignedLightConversationCellSemanticStyles: CellSemanticStyles = merge(
  cloneDeep(lightCellSemanticStyles),
  {
    summaryUnreadText: lightTypographySemanticStyles.shades.secondary,
    dividerColor: lightBorderSemanticStyles.tertiary,
    unreadIndicator: {
      backgroundColor: colorPrimitive.blurple[600],
      hoverBackgroundColor: colorPrimitive.blurple[300],
    },
    background: {
      default: colorPrimitive.alpha.transparent,
      hover: colorPrimitive.alpha.black.black100,
      selected: colorPrimitive.blurple['100'],
    },
    border: {
      default: alphas.transparent,
      selected: colorPrimitive.blurple['400'],
    },
    overlayBackground: greys.shade20,
  } satisfies Partial<CellSemanticStyles>,
);

export const redesignedDarkConversationCellSemanticStyles: CellSemanticStyles = merge(
  cloneDeep(darkCellSemanticStyles),
  {
    summaryUnreadText: darkTypographySemanticStyles.shades.secondary,
    dividerColor: redesignedDarkBorderSemanticStyles.tertiary,
    unreadIndicator: {
      backgroundColor: colorPrimitive.blurple[500],
      hoverBackgroundColor: colorPrimitive.blurple[300],
    },
    background: {
      default: colorPrimitive.alpha.transparent,
      hover: colorPrimitive.alpha.white.white100,
      selected: colorPrimitive.alpha.white.white100,
    },
    border: {
      default: alphas.transparent,
      selected: colorPrimitive.blurple['500'],
    },
  } satisfies Partial<CellSemanticStyles>,
);
